// SignUp.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, ArrowRight, Lock, Eye, EyeOff, CheckCircle2, XCircle, Clock, MailCheck, User, Briefcase, FileText, Download } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ndaRequired, setNdaRequired] = useState(true);
  const [userType, setUserType] = useState(null); // 'cedant' ou 'repreneur'
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    number: false,
    uppercase: false,
    lowercase: false,
    special: false,
  });

  // Récupérer les données depuis l'état de navigation
  const userData = useMemo(() => location.state?.formData || {}, [location.state?.formData]);
  const userTypeFromState = location.state?.userType || null;

  // Déterminer le type d'utilisateur au chargement
  useEffect(() => {
    if (userTypeFromState) {
      setUserType(userTypeFromState);
    } else if (userData.valuation) {
      // Si les données contiennent 'valuation', c'est un cédant
      setUserType('cedant');
    } else if (userData.qualification) {
      // Si les données contiennent 'qualification', c'est un repreneur
      setUserType('repreneur');
    }

    // Fetch public settings
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        setNdaRequired(response.data.nda_required);
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres", error);
      }
    };
    fetchSettings();
  }, [userTypeFromState, userData]);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    acceptTerms: false,
    // acceptNewsletter: false,
  });

  const handleChange = (field, value) => {
    if (field === "password") {
      setFormData(prev => ({ ...prev, [field]: value }));
      checkPasswordStrength(value);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const checkPasswordStrength = (password) => {
    const checks = {
      length: password.length >= 8,
      number: /[0-9]/.test(password),
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    setPasswordChecks(checks);

    // Calculer la force du mot de passe (0-4)
    const strength = Object.values(checks).filter(Boolean).length;
    setPasswordStrength(strength);
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return "bg-red-500";
      case 2:
        return "bg-orange-500";
      case 3:
        return "bg-yellow-500";
      case 4:
        return "bg-green-500";
      default:
        return "bg-gray-200";
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return "Très faible";
      case 2:
        return "Faible";
      case 3:
        return "Moyen";
      case 4:
        return "Fort";
      default:
        return "";
    }
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return false;
    }

    if (passwordStrength < 3) {
      toast.error("Veuillez choisir un mot de passe plus fort");
      return false;
    }

    if (!formData.acceptTerms) {
      toast.error("Vous devez accepter les conditions d'utilisation");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Combiner les données utilisateur avec les données de connexion
      const payload = {
        ...userData,
        mot_de_passe: formData.password,
        // acceptNewsletter: formData.acceptNewsletter,

      };

      // Choisir le bon endpoint selon le type d'utilisateur (à revoir selon backend)
      let endpoint = '';
      if (userType === 'cedant') {
        endpoint = `${API}/cedants`;
      } else if (userType === 'repreneur') {
        endpoint = `${API}/repreneurs`;
      }
      // else {
      //   endpoint = `${API}/users`; 
      //   Endpoint générique
      // }
      console.log("data", payload)
      const response = await axios.post(endpoint, payload);

      // Simulation pour le développement
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Afficher la page de confirmation
      setIsSubmitted(true);

      // Stocker les informations temporairement
      localStorage.setItem("pending_user", JSON.stringify({
        email: userData.email,
        userType: userType,
        message: "Compte en attente de validation"
      }));

    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Erreur lors de la création du compte");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    // Rediriger vers le bon simulateur selon le type d'utilisateur
    if (userType === 'cedant') {
      navigate("/cedant", { state: { formData: userData, fromSignUp: true } });
    } else if (userType === 'repreneur') {
      navigate("/repreneur", { state: { formData: userData, fromSignUp: true } });
    } else {
      navigate("/");
    }
  };

  const handleGoHome = () => {
    navigate("/");
  };

  // Page de confirmation après soumission
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background py-8 px-4" data-testid="signup-confirmation">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={handleGoHome}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Accueil
            </button>
            <span className="text-2xl font-bold text-primary font-['Playfair_Display']">Sequenti</span>
          </div>

          {/* Confirmation Card */}
          <Card className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <MailCheck className="w-10 h-10 text-green-600" />
            </div>

            <h1 className="text-2xl font-semibold font-['Playfair_Display'] mb-3">
              Compte créé avec succès !
            </h1>

            <p className="text-muted-foreground mb-6">
              Merci pour votre intérêt pour Sequenti. Votre demande de création de compte a bien été prise en compte.
            </p>

            <Alert className="bg-blue-50 border-blue-200 mb-6">
              <Clock className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Votre compte est en cours de validation par notre équipe. Vous recevrez un email de confirmation dès que votre compte sera approuvé.
              </AlertDescription>
            </Alert>

            <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-medium mb-2">Prochaines étapes :</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Notre équipe examine votre dossier (délai moyen : 24-48h)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Vous recevrez un email de confirmation à <strong>{userData.email}</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>
                    {userType === 'cedant'
                      ? "Une fois approuvé, vous pourrez accéder à votre estimation personnalisée"
                      : "Une fois approuvé, vous pourrez accéder à votre évaluation de profil"}
                  </span>
                </li>
              </ul>
            </div>

            {userType === 'repreneur' && ndaRequired && (
              <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-center gap-2 mb-3 text-orange-800">
                  <FileText className="w-5 h-5" />
                  <h3 className="font-semibold">Action requise : Signature de la NDA</h3>
                </div>
                <p className="text-sm text-orange-700 mb-4">
                  Pour valider votre compte, vous devez signer notre accord de confidentialité (NDA).
                </p>
                <div className="space-y-3">
                  <a
                    href="https://sequenti-files.s3.eu-west-3.amazonaws.com/NDA/NDA+Sequenti.docx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2 bg-white border border-orange-200 rounded-md text-sm font-medium text-orange-700 hover:bg-orange-50 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Télécharger la NDA
                  </a>
                  <p className="text-xs text-orange-600 italic">
                    Une fois le document rempli et signé, merci de l'envoyer par email à <strong>eric@sequenti.fr</strong>
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Button
                onClick={handleGoHome}
                className="w-full bg-primary text-white hover:bg-primary/90"
              >
                Retour à l'accueil
              </Button>

              <p className="text-xs text-muted-foreground">
                Vous avez des questions ? Contactez-nous à{" "}
                <a href="mailto:support@sequenti.fr" className="text-primary hover:underline">
                  support@sequenti.fr
                </a>
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Formulaire d'inscription
  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="signup-page">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <span className="text-2xl font-bold text-primary font-['Playfair_Display']">Sequenti</span>
        </div>

        {/* Main Card */}
        <Card className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              {userType === 'cedant' ? (
                <Briefcase className="w-8 h-8 text-primary" />
              ) : userType === 'repreneur' ? (
                <User className="w-8 h-8 text-primary" />
              ) : (
                <Lock className="w-8 h-8 text-primary" />
              )}
            </div>
            <h1 className="text-2xl font-semibold font-['Playfair_Display'] mb-2">
              Créez votre compte {userType === 'cedant' ? 'de cédant' : userType === 'repreneur' ? 'de repreneur' : ''}
            </h1>
            <p className="text-muted-foreground text-sm">
              {userType === 'cedant'
                ? "Pour accéder à votre estimation personnalisée et suivre votre projet de cession"
                : userType === 'repreneur'
                  ? "Pour accéder à votre évaluation et suivre vos opportunités de reprise"
                  : "Pour accéder à votre espace personnalisé"}
            </p>
          </div>

          <div className="space-y-6">
            {/* Email (pré-rempli) */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={userData.email || ""}
                disabled
                className="bg-muted"
                data-testid="input-email"
              />
              <p className="text-xs text-muted-foreground">
                Cet email servira d'identifiant pour vous connecter
              </p>
            </div>

            {/* Mot de passe */}
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder="Créez un mot de passe sécurisé"
                  className="pr-10"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Barre de force du mot de passe */}
              {formData.password && (
                <div className="space-y-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Force du mot de passe:
                    </span>
                    <span className="text-xs font-medium">
                      {getPasswordStrengthText()}
                    </span>
                  </div>
                  <Progress
                    value={(passwordStrength / 4) * 100}
                    className={`h-1 ${getPasswordStrengthColor()}`}
                  />
                </div>
              )}

              {/* Critères de mot de passe */}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="flex items-center gap-2 text-xs">
                  {passwordChecks.length ? (
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                  ) : (
                    <XCircle className="w-3 h-3 text-muted-foreground" />
                  )}
                  <span className={passwordChecks.length ? "text-green-600" : "text-muted-foreground"}>
                    8 caractères minimum
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {passwordChecks.number ? (
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                  ) : (
                    <XCircle className="w-3 h-3 text-muted-foreground" />
                  )}
                  <span className={passwordChecks.number ? "text-green-600" : "text-muted-foreground"}>
                    1 chiffre
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {passwordChecks.uppercase ? (
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                  ) : (
                    <XCircle className="w-3 h-3 text-muted-foreground" />
                  )}
                  <span className={passwordChecks.uppercase ? "text-green-600" : "text-muted-foreground"}>
                    1 majuscule
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {passwordChecks.lowercase ? (
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                  ) : (
                    <XCircle className="w-3 h-3 text-muted-foreground" />
                  )}
                  <span className={passwordChecks.lowercase ? "text-green-600" : "text-muted-foreground"}>
                    1 minuscule
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {passwordChecks.special ? (
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                  ) : (
                    <XCircle className="w-3 h-3 text-muted-foreground" />
                  )}
                  <span className={passwordChecks.special ? "text-green-600" : "text-muted-foreground"}>
                    1 caractère spécial
                  </span>
                </div>
              </div>
            </div>

            {/* Confirmation du mot de passe */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  placeholder="Confirmez votre mot de passe"
                  className="pr-10"
                  data-testid="input-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-xs text-red-500">
                  Les mots de passe ne correspondent pas
                </p>
              )}
            </div>

            {/* Conditions d'utilisation */}
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="acceptTerms"
                  checked={formData.acceptTerms}
                  onCheckedChange={(checked) => handleChange("acceptTerms", checked)}
                  data-testid="checkbox-terms"
                />
                <Label htmlFor="acceptTerms" className="text-sm font-normal leading-tight">
                  J'accepte les{" "}
                  <a className="text-primary hover:underline" target="_blank">
                    {/* <a href="/" className="text-primary hover:underline" target="_blank"> */}
                    conditions d'utilisation
                  </a>{" "}
                  et la{" "}
                  <a className="text-primary hover:underline" target="_blank">
                    {/* <a href="/" className="text-primary hover:underline" target="_blank"> */}
                    politique de confidentialité
                  </a>
                </Label>
              </div>

              {/* <div className="flex items-start space-x-3">
                <Checkbox
                  id="acceptNewsletter"
                  checked={formData.acceptNewsletter}
                  onCheckedChange={(checked) => handleChange("acceptNewsletter", checked)}
                  data-testid="checkbox-newsletter"
                />
                <Label htmlFor="acceptNewsletter" className="text-sm font-normal text-muted-foreground">
                  Je souhaite recevoir des conseils et actualités sur la transmission d'entreprise
                </Label>
              </div> */}
            </div>

            {/* Alert sur le processus de validation */}
            <Alert className="bg-blue-50 border-blue-200">
              <Clock className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800 text-sm">
                Votre compte sera soumis à validation par notre équipe avant activation.
                Vous serez notifié par email dès que votre compte sera approuvé.
              </AlertDescription>
            </Alert>

            {/* Résumé des données selon le type d'utilisateur */}
            {userData && Object.keys(userData).length > 0 && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="text-sm font-medium mb-2">Récapitulatif :</h3>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {userType === 'cedant' && userData.valuation && (
                    <>
                      <li>• Secteur : {userData.valuation.secteur}</li>
                      <li>• CA : {userData.valuation.chiffre_affaires}</li>
                      <li>• Effectif : {userData.valuation.effectif}</li>
                      <li>• Ancienneté : {userData.valuation.anciennete}</li>
                    </>
                  )}
                  {userType === 'repreneur' && userData.qualification && (
                    <>
                      <li>• Capacité : {userData.qualification.capacite_financiere}</li>
                      <li>• Expérience : {userData.qualification.experience}</li>
                      <li>• Secteurs ciblés : {userData.qualification.secteurs_cibles?.length || 0} secteurs</li>
                      <li>• Budget : {userData.qualification.budget_max || "Non spécifié"}</li>
                      <li>• Secteur actuel : {userData.secteur_activite || "Non spécifié"}</li>
                    </>
                  )}
                </ul>
              </div>
            )}

            {/* Bouton de soumission */}
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.password || !formData.confirmPassword || !formData.acceptTerms}
              className="w-full bg-primary text-white hover:bg-primary/90"
              data-testid="submit-button"
            >
              {isSubmitting ? (
                "Création en cours..."
              ) : (
                <>
                  Créer mon compte
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}